/**
 * What a signed-in player's statistics and calendar are built from (ACC-011, owner
 * 2026-09-27): the ACCOUNT's days, as the server replayed them, not this browser's.
 *
 * The browser's history is the device's, and signing in used to copy the account's days
 * into it. Signing in as someone else then showed the last person's days, and on a
 * shared device anyone who signed in saw them. Now, signed in, the view is the account's
 * days from `/api/me/history`, with only TODAY taken from this device: the attempt in
 * progress, or one just finished whose last shots the server has not seen yet. Signed
 * out, it is this device's history, as before.
 *
 * Pure, so it is tested without a browser or a server.
 */
import { mergeDay } from './account-sync.ts';
import type { DayRecord, History } from './history.ts';

export function accountView(
  accountDays: readonly DayRecord[],
  device: Pick<History, 'get'>,
  todayKey: string | undefined,
): readonly DayRecord[] {
  const days = new Map(accountDays.map((d) => [d.dateKey, d]));
  if (todayKey !== undefined) {
    const mine = device.get(todayKey);
    const server = days.get(todayKey);
    if (mine !== undefined)
      days.set(todayKey, server === undefined ? mine : mergeDay(mine, server));
  }
  return [...days.values()].sort((a, b) => (a.dateKey < b.dateKey ? -1 : 1));
}
